import React from 'react';
import { Bike, Building2, Trees, Mountain, Waves, Droplets, Car, Scan, LandPlot } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import ColorSettingsPopup from './ColorSettingsPopup';

const ColorLegend = ({
  settings,
  direction = 'horizontal',
  onColorSettingChange,
  onActivityTypeSettingChange,
  activitySource,
}) => {
  const categoryIcons = {
    activities: { icon: Bike, help: "Activities" },
    land: { icon: LandPlot, help: "Land" },
    buildings: { icon: Building2, help: "Buildings" },
    forests: { icon: Trees, help: "Forests / woods" },
    naturalareas: { icon: Mountain, help: "Parks / reserves" },
    oceans: { icon: Waves, help: "Oceans" },
    water: { icon: Droplets, help: "Water / rivers / lakes" },
    streets: { icon: Car, help: "Roads / paths" },
  };

  const sortedCategories = Object.entries(categoryIcons).sort((a, b) => {
    const categoryA = a[0];
    const categoryB = b[0];
    const isActiveA = settings[categoryA]?.active;
    const isActiveB = settings[categoryB]?.active;

    if (isActiveA && !isActiveB) return -1;
    if (!isActiveA && isActiveB) return 1;
    return 0;
  });

  if (direction === 'horizontal') {
    // Horizontal layout (original)
    return (
      <TooltipProvider>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-4 mb-2">
          {sortedCategories.map(([category, { icon: Icon, help }]) => (
            <Sheet key={category}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SheetTrigger className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity">
                    <div
                      className="relative flex items-center gap-1"
                      style={{ opacity: settings[category].active ? 1 : 0.15 }}
                    >
                      <Icon className="h-3 w-3 lg:h-4 lg:w-4" />
                      <div
                        className="h-3 w-3 lg:h-4 lg:w-4 rounded-full border border-gray-400"
                        style={{
                          backgroundColor: settings[category].color,
                          opacity: settings[category].alpha
                        }}
                      />
                      {/* <p className='text-sm text-right'>{help}</p> */}
                    </div>
                  </SheetTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {!settings[category]?.active ? <><strong>Disabled</strong> - {help}</> : help}
                  </p>
                </TooltipContent>
              </Tooltip>
              <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Edit {category} Settings</SheetTitle>
                </SheetHeader>
                <div className="py-4">
                  <ColorSettingsPopup
                    settings={settings}
                    onColorSettingChange={onColorSettingChange}
                    onActivityTypeSettingChange={onActivityTypeSettingChange}
                    activitySource={activitySource}
                    triggerButton={false}
                  />
                </div>
              </SheetContent>
            </Sheet>
          ))}
        </div>
      </TooltipProvider>
    );
  } else {
    // Vertical layout with two columns
    // Split into first and second column
    const firstColumn = sortedCategories.slice(0, 4);
    const secondColumn = sortedCategories.slice(4);

    return (
      <TooltipProvider>
        <div className="flex gap-6 mt-4 mb-2">
          <div className="flex flex-col gap-3">
            {firstColumn.map(([category, { icon: Icon, help }]) => (
              <Sheet key={category}>
                <SheetTrigger className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity">
                  <div
                    className="relative flex items-center gap-1"
                    style={{ opacity: settings[category].active ? 1 : 0.15 }}
                  >
                    <Icon className="h-3 w-3 lg:h-4 lg:w-4" />
                    <div
                      className="h-3 w-3 lg:h-4 lg:w-4 rounded-full border border-gray-400"
                      style={{
                        backgroundColor: settings[category].color,
                        opacity: settings[category].alpha
                      }}
                    />
                    <p className='text-xs'>{help}</p>
                  </div>
                </SheetTrigger>
                <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Edit {category} Settings</SheetTitle>
                  </SheetHeader>
                  <div className="py-4">
                    <ColorSettingsPopup
                      settings={settings}
                      onColorSettingChange={onColorSettingChange}
                      triggerButton={false}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {secondColumn.map(([category, { icon: Icon, help }]) => (
              <Sheet key={category}>
                <SheetTrigger className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity">
                  <div
                    className="relative flex items-center gap-1"
                    style={{ opacity: settings[category].active ? 1 : 0.15 }}
                  >
                    <Icon className="h-3 w-3 lg:h-4 lg:w-4" />
                    <div
                      className="h-3 w-3 lg:h-4 lg:w-4 rounded-full border border-gray-400"
                      style={{
                        backgroundColor: settings[category].color,
                        opacity: settings[category].alpha
                      }}
                    />
                    <p className='text-xs'>{help}</p>
                  </div>
                </SheetTrigger>
                <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Edit {category} Settings</SheetTitle>
                  </SheetHeader>
                  <div className="py-4">
                    <ColorSettingsPopup
                      settings={settings}
                      onColorSettingChange={onColorSettingChange}
                      triggerButton={false}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            ))}
          </div>
        </div>
      </TooltipProvider>
    );
  }
};

export default ColorLegend;